import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import {
  Camera, ShoppingCart, Star, Heart, ChevronRight, Zap, Eye, Package
} from 'lucide-react'

const AFFILIATE_LINK = 'https://www.amazon.com/dp/B0G834TQB4?pd_rd_i=B0G834TQB4&pd_rd_w=ZULVk&content-id=amzn1.sym.8c2f9165-8e93-42a1-8313-73d3809141a2&pf_rd_p=8c2f9165-8e93-42a1-8313-73d3809141a2&pf_rd_r=EG9FB0JEPRQXSZC37AJ1&pd_rd_wg=TKxVO&pd_rd_r=cbf9241b-7219-465d-b4d7-5d61c38d22fa&sp_csd=d2lkZ2V0TmFtZT1zcF9kZXRhaWw&th=1&linkCode=ll1&tag=homeinhouse-20&linkId=0736188751f315773494f5e91f97c981&language=en_US&ref_=as_li_ss_tl'

export default function MiniDigitalCameraPage() {
  return (
    <>
      <Helmet>
        <title>Mini Digital Camera - Tiny Pocket Keychain Camera for Everyday Photography | G6 Optics</title>
        <meta name="description" content="The G6 mini digital camera weighs just 26g—the ultimate tiny digital camera and keychain camera for everyday carry. Best pocket digital camera with thumb camera portability." />
        <link rel="canonical" href="https://g6optics.com/mini-digital-camera" />
        <meta property="og:title" content="Mini Digital Camera - Pocket Keychain Camera" />
        <meta property="og:description" content="26 gram tiny digital camera that clips to your keychain. The ultimate pocket digital camera for everyday adventures." />
      </Helmet>

      {/* Hero Section */}
      <section className="page-hero pixel-texture">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="page-hero-content"
          >
            <span className="hero-badge retro-border-sm"><Star size={12} /> ULTRA PORTABLE</span>
            <h1 className="page-hero-title">
              Mini Digital Camera: The <span className="hero-title-accent">Tiny Camera</span> That Goes Everywhere
            </h1>
            <p className="page-hero-desc">
              Looking for a <strong>mini digital camera</strong> that actually fits your lifestyle? The 
              <Link to="/g6-thumb-camera"> G6 thumb camera</Link> weighs just 26 grams—making it the lightest 
              <em> tiny digital camera</em> in its class. This <em>pocket digital camera</em> clips to your 
              keychain so you never miss a moment.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="use-cases-section pixel-dots">
        <div className="container">
          <h2 className="section-title">Perfect Moments for Your Mini Digital Camera</h2>
          <p className="section-desc">
            The G6 <strong>keychain camera</strong> is designed for spontaneous photography. Here's where 
            this <em>tiny digital camera</em> shines.
          </p>
          
          <div className="use-cases-grid">
            <motion.div 
              className="use-case-card retro-border-sm"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="use-case-icon">🏃</div>
              <h3>Active Adventures</h3>
              <p>
                Running, hiking, biking—activities where every gram matters. This <strong>pocket digital 
                camera</strong> won't slow you down. Clip it to your shorts and capture the trail without 
                stopping to dig through a bag.
              </p>
            </motion.div>

            <motion.div 
              className="use-case-card retro-border-sm"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <div className="use-case-icon">🎉</div>
              <h3>Social Events</h3>
              <p>
                Parties, concerts, nights out—moments where your phone is too precious to risk. The G6 
                <em> mini digital camera</em> costs less than a phone screen repair and captures better 
                vibes than any smartphone.
              </p>
            </motion.div>

            <motion.div 
              className="use-case-card retro-border-sm"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <div className="use-case-icon">✈️</div>
              <h3>Travel Light</h3>
              <p>
                Minimalist travelers rejoice. This <strong>thumb camera</strong> replaces the bulky camera 
                you'd leave at home anyway. Slip it in your pocket and document your journey with 
                <Link to="/retro-mini-camera"> authentic retro style</Link>.
              </p>
            </motion.div>

            <motion.div 
              className="use-case-card retro-border-sm"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              <div className="use-case-icon">📸</div>
              <h3>Everyday Carry</h3>
              <p>
                The best camera is the one you have with you. As a <strong>keychain camera</strong>, the G6 
                is always there—hanging from your keys, bag, or lanyard. Never miss a moment because you 
                "didn't bring a camera."
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="content-section pixel-texture">
        <div className="container">
          <div className="content-grid">
            <article className="main-content">
              <h2>What Makes a Great Mini Digital Camera?</h2>
              <p>
                A true <strong>mini digital camera</strong> must balance three factors: size, image quality, and 
                usability. Many so-called "mini" cameras are still too bulky to carry everywhere, or they 
                sacrifice image quality for portability. The <Link to="/g6-thumb-camera">G6 thumb camera</Link> 
                achieves all three:
              </p>
              
              <div className="feature-highlights">
                <div className="feature-highlight">
                  <Zap size={24} />
                  <div>
                    <h4>True Pocket Size</h4>
                    <p>At 45×35×25mm and 26 grams, the G6 is smaller than a box of matches. Most "pocket" 
                    cameras require a jacket pocket—the G6 fits in jeans.</p>
                  </div>
                </div>
                <div className="feature-highlight">
                  <Camera size={24} />
                  <div>
                    <h4>Real Image Quality</h4>
                    <p>CCD sensor with 12MP resolution and 1080p video. This isn't a novelty toy—it's a 
                    legitimate <Link to="/retro-mini-camera">film point and shoot style camera</Link> that produces printable images.</p>
                  </div>
                </div>
                <div className="feature-highlight">
                  <Eye size={24} />
                  <div>
                    <h4>Point and Shoot Simplicity</h4>
                    <p>One button operation. No menus to navigate, no settings to fumble with. Just aim 
                    and shoot like a <em>pocket digital camera</em> should work.</p>
                  </div>
                </div>
              </div>

              <h2>Mini Digital Camera vs. Smartphone Photography</h2>
              <p>
                Why carry a dedicated <strong>tiny digital camera</strong> when your phone has a camera? 
                Here's what photographers discover after switching to the G6:
              </p>
              <ul className="feature-list">
                <li>
                  <strong>No distractions:</strong> A <em>pocket digital camera</em> only takes photos. 
                  No notifications, no social media, no "just checking one thing" that turns into 30 minutes.
                </li>
                <li>
                  <strong>Better for the moment:</strong> Phones encourage perfection—multiple takes, filters, 
                  immediate posting. The G6 encourages presence and authenticity.
                </li>
                <li>
                  <strong>Unique aesthetic:</strong> Everyone's phone photos look the same. The G6's CCD sensor 
                  delivers a <Link to="/retro-mini-camera">vintage film camera style</Link> look that stands out.
                </li>
                <li>
                  <strong>Risk-free:</strong> Drop a phone and it's a $200+ repair. Drop the G6 and you 
                  brush it off. Bring it to the beach, the concert, the party.
                </li>
                <li>
                  <strong>Always available:</strong> As a <strong>keychain camera</strong>, the G6 is literally 
                  attached to your keys. Phone dead? Lost your bag? Your camera is still with you.
                </li>
              </ul>

              <h2>The Thumb Camera Form Factor Explained</h2>
              <p>
                The term <strong>thumb camera</strong> describes cameras small enough to operate with your 
                thumb. The G6 takes this literally: one-handed operation with a single shutter button. 
                This <em>tiny digital camera</em> design philosophy prioritizes spontaneity over control.
              </p>
              <p>
                Traditional cameras assume you'll compose carefully and adjust settings. The G6 assumes 
                you'll see something amazing and want to capture it <em>right now</em>. It's the difference 
                between a tool for photography and a tool for living photographically.
              </p>

              <h2>Best Uses for a Keychain Camera</h2>
              <p>
                The <strong>keychain camera</strong> concept is revolutionary because it changes when you 
                take photos. Instead of "bring a camera" being a decision, it's the default. G6 owners report 
                photographing moments they would have missed with any other setup:
              </p>
              <ul className="feature-list">
                <li>Street scenes on the walk to work</li>
                <li>Spontaneous moments with friends</li>
                <li>Wildlife and nature on daily runs</li>
                <li>Documentary-style life coverage</li>
                <li>Kids and pets being themselves</li>
              </ul>
            </article>

            <aside className="sidebar">
              <div className="sidebar-card retro-border">
                <h3>Size Comparison</h3>
                <ul>
                  <li><strong>G6:</strong> 26g, keychain-sized</li>
                  <li><strong>GoPro:</strong> 153g, pocket-sized</li>
                  <li><strong>Point & Shoot:</strong> 200g+</li>
                  <li><strong>iPhone:</strong> 206g</li>
                </ul>
                <p className="sidebar-note">The G6 is 8× lighter than a typical smartphone!</p>
              </div>

              <div className="sidebar-card retro-border">
                <h3>Quick Buy</h3>
                <a
                  href={AFFILIATE_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-primary retro-border sidebar-cta"
                >
                  <ShoppingCart size={16} />
                  Get G6 Mini Camera
                </a>
                <p className="sidebar-note">Available in 9 colors on Amazon</p>
              </div>

              <div className="sidebar-card retro-border">
                <h3>Related Pages</h3>
                <ul className="sidebar-links">
                  <li><Link to="/g6-thumb-camera">Full G6 Camera Details</Link></li>
                  <li><Link to="/retro-mini-camera">Retro Camera Style Guide</Link></li>
                  <li><Link to="/faq">FAQ</Link></li>
                </ul>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Portability Features */}
      <section className="portability-section pixel-dots">
        <div className="container">
          <h2 className="section-title">Built for True Portability</h2>
          <div className="portability-grid">
            <div className="portability-card retro-border-sm">
              <Package size={32} />
              <h3>Keychain Mount</h3>
              <p>Built-in clip attaches to keys, bags, lanyards, or belt loops. Your <strong>mini digital camera</strong> is always within reach.</p>
            </div>
            <div className="portability-card retro-border-sm">
              <Zap size={32} />
              <h3>26 Gram Weight</h3>
              <p>Lighter than most keychains. You'll forget you're carrying a <em>pocket digital camera</em> until you need it.</p>
            </div>
            <div className="portability-card retro-border-sm">
              <Camera size={32} />
              <h3>No Case Needed</h3>
              <p>Durable ABS plastic body handles everyday carry. Throw this <em>tiny digital camera</em> in any bag without worry.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta pixel-texture">
        <div className="container">
          <motion.div
            className="cta-inner"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="cta-badge retro-border-sm">
              <Heart size={12} style={{ fill: 'currentColor' }} />
              The World's Most Portable Camera
            </div>

            <h2 className="cta-title">
              Get Your <span className="cta-title-accent">Mini Digital Camera</span> Today
            </h2>

            <p className="cta-desc">
              Stop missing moments because your camera was too bulky or too precious to bring. 
              The G6 thumb camera goes everywhere you do—on your keychain.
            </p>

            <a
              href={AFFILIATE_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary retro-border"
            >
              <ShoppingCart size={18} />
              Buy G6 Mini Camera on Amazon
              <ChevronRight size={18} />
            </a>

            <p className="cta-fine">Free shipping • 30-day returns • 1-year warranty</p>
          </motion.div>
        </div>
      </section>
    </>
  )
}
